import React, {useState} from 'react';
import {compose} from 'redux';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {_openModal} from '../../../../shared/reducers/modals';
import {Button, Checkbox, Column, DescriptiveText, EMoneyPoS, Icon, LoadingSpinner, MgpPoS, Row} from '../../../../shared';
import Table from '../../../../shared/components/Table';
import debug from 'debug';
import {formatter, gatherData, getRiskLabel, getUpdatedFilter} from '../../../../shared/utils/utils';
import useQueryObject from '../../../../shared/utils/useQueryObject';
import {getRiskPartner, isAuthorized, isEntitled} from '../../../../shared/reducers/user';
import {withSize} from 'react-sizeme';
import {openRiskPartner} from '../../../utils/partners';
import RiskBox from '../../../../shared/components/analyze/RiskBox';
import {mgpLaunch} from '../../../sections/analyze/Mgp';
import axiosInstance from '../../../../shared/utils/axios';
import history from '../../../../history';
import useSWR from 'swr';
import HouseholdActivationButton from '../../components/HouseholdActivationButton';

const log = debug('circleblack:advisor:analyze:households:table');

const HouseholdsTable = ({
  context,
  openModal,
  size,
  onRowClick,
  rowsPerPage,
  maxHeight,
  height = 'fit-content',
  session,
  rowOnClickDisabled,
  fullPage,
}) => {
  const [filters, setFilters] = useQueryObject('householdTableFilters', {});
  const gatherHouseholds = context && gatherData(`/containers/${context.containerID}/clients`);
  const [deactivatedNote, setDeactivatedNote] = useState(null);
  const entitledMgp = isEntitled([{group: 'financialPlanning', feature: 'mgp', access: 'r'}]);
  const entitledEMoney = isEntitled([{group: 'financialPlanning', feature: 'emoney', access: 'r'}]);

  const dataManager = {
    gatherData: inputValue => gatherHouseholds({...inputValue, ...getUpdatedFilter(filters)}),
  };

  React.useEffect(() => {
    if (dataManager.refresh) {
      dataManager.refresh();
    }
  }, [context, filters]);

  const advisorsButton = h => <Icon
    svg={'advisorsCircle'}
    disabled={!h.advisors || !h.advisors || !h.containerID}
    action={() => openModal({
      type: 'ContainerAdvisorsModal',
      container: {id: h.containerID, name: h.displayName},
    })}/>;

  const updateFilters = (name, value) => setFilters({...filters, [name]: value});

  const riskPartner = getRiskPartner();

  const riskNumber = household => <RiskBox
    action={() => openRiskPartner({
      totumID: (household.containers?.ALL?.riskNumber !== null) ? household.id : undefined,
      householdID: (riskPartner !== 'stratifi') ? household.id : undefined,
      containerID: (riskPartner === 'stratifi') ? household.containerID : undefined,
    })}
    riskPartner={riskPartner}
    riskNumber={household.containers && household.containers.ALL && household.containers.ALL.riskNumber}
    type='portfolio'
  />;

  const viewAsInvestor = household => <>{isEntitled([{group: 'advisor', feature: 'view_as_investor', access: 'r'}])
    ? <Button importance={'secondary'}
              size={'sm'}
              label={'Investor Experience'}
              width={'130px'}
              action={() => history.push(`/investor/${household.id}`)}/> : null}</>;

  const lastLogin = household => household.lastLogin
    ? <div style={{display: 'inline-block'}}>
      <DescriptiveText width={'130px'} margin={'5px'} alignment={'center'}>
        {formatter('datetime')(household.lastLogin)}
      </DescriptiveText>
    </div>
    : <>
      <HouseholdActivationButton
            disabled={(!!household.totalLogins && household.activatedAt) || !household.isAdvisor}
            size='sm'
            width={'130px'}
            household={household}
      />
    </>;

  const renderLastLogin = household => <>
    {lastLogin(household)}
    {viewAsInvestor(household)}
  </>;

  React.useEffect(() => {
    if (!context) {
      return;
    }
    switch (context.type) {
      case 'ADVISOR':
        if (isAuthorized(['ADVISOR'])) {
          setDeactivatedNote('');
        } else {
          setDeactivatedNote(null);
          if (context.id !== session.profile.id) {
            axiosInstance.get(`/crm/advisors?limit=1&id=${context.id}`)
              .then(res => {
                if (!res.data[0]?.activatedAt) {
                  return setDeactivatedNote(`Note: You cannot navigate to the Household Overview from an inactive advisor's view. To see ${context.name}'s view of their households, please activate them as an advisor in the Onboarding section.`);
                }
                setDeactivatedNote('');
              });
          } else if (!session.profile.activatedAt) {
            setDeactivatedNote(`Note: You cannot navigate to the Household Overview from an inactive advisor's view. To see ${context.name}'s view of their households, please activate them as an advisor in the Onboarding section.`);
          } else {
            setDeactivatedNote('');
          }
        }
        break;
      case 'BRANCH':
        if (isAuthorized('BRANCHMGR') && session.profile.activatedAt) {
          setDeactivatedNote('');
        } else {
          setDeactivatedNote(null);
          axiosInstance.get(`/branches/${context.id}/managers?limit=1&isNotNull=activatedAt`)
            .then(res => {
              if (!res.data.length) {
                return setDeactivatedNote(`Note: You cannot navigate to the Household Overview from an inactive branch's view. To see ${context.name}'s view of their households, please activate a Branch Manager in the Onboarding section.`);
              }
              setDeactivatedNote('');
            });
        }
        break;
      default:
        setDeactivatedNote('');
    }
  }, [context]);

  log('rendering');

  if (!context || deactivatedNote === null) {
    return <LoadingSpinner/>;
  }

  return <Column layout={{grow: 1}}>
    {deactivatedNote && <DescriptiveText>{deactivatedNote}</DescriptiveText>}
    <Table dataManager={dataManager}
           key={'something'}
           columns={[
             {
               title: 'Household',
               dataPoint: 'displayName',
               type: 'text',
               minWidth: 120,
               isSortable: true,
             },
             (fullPage || size.width > 500) && {
               title: 'Clients',
               dataPoint: 'clientCounts',
               type: 'number',
               minWidth: 60,
               isSortable: true,
             },
             (fullPage || size.width > 500) && {
               title: 'Accounts',
               dataPoint: 'totalAccounts',
               type: 'number',
               minWidth: 80,
               isSortable: true,
             },
             {
               title: 'Assets',
               dataPoint: 'amount',
               type: 'currency',
               minWidth: 100,
               isSortable: true,
             },
             (fullPage || size.width > 600) && ['FIRM', 'BRANCH'].includes(context.type) && {
               title: 'Advisors',
               dataPoint: 'advisors',
               type: 'custom',
               render: advisorsButton,
               minWidth: 50,
               isSortable: false,
             },
             (fullPage || size.width > 440) && context.type === 'ADVISOR' && (entitledMgp || entitledEMoney) && {
               title: 'Probability of Success',
               dataPoint: 'probabilityOfSuccessPercent',
               type: 'custom',
               render: function processRow(row) {
                 if (entitledMgp) {
                   return <Row>
                     <Column height={'40px'} width={'75px'} justifySubColumn={'middle'} alignItems={'center'}>
                       <MgpPoS isScalable={false} action={() => mgpLaunch(row.id, openModal)} probabilityOfSuccess={row.probabilityOfSuccess} showLogo={false} layoutSize={'xs'}/>
                     </Column>
                   </Row>;
                 }
                 if (entitledEMoney) {
                   const {data: info} = useSWR(`/households/${row.id}/emoney`);
                   const probability = info?.probabilityOfSuccess;
                   const eMoneyPoS = probability ? Number(probability.substring(0, probability.length - 1)) : 0;
                   const error = eMoneyPoS?.text?.split(':').includes('Error');
                   const {data: queue} = useSWR(`/households/${row.id}/emoney/status`);
                   const isQueued = queue?.isQueued;
                   return <Row>
                     <Column maxHeight={'100px'} width={'120px'} justifySubColumn={'middle'} alignItems={'center'}>
                       <EMoneyPoS isScalable={false} householdId={row.id} probabilityOfSuccess={eMoneyPoS} showLogo={false} isQueued={isQueued} error={error} layoutSize={'xs'}/>
                     </Column>
                   </Row>;
                 }
               },
               minWidth: 50,
               isSortable: true,
             },
             (fullPage || size.width > 380) && riskPartner && context.type === 'ADVISOR' && {
               title: getRiskLabel(riskPartner),
               dataPoint: 'riskNumber',
               type: 'custom',
               render: riskNumber,
               minWidth: 50,
               isSortable: false,
             },
             (fullPage || size.width > 600) && {
               title: 'Last Login',
               dataPoint: 'lastLogin',
               type: 'datetime',
               render: renderLastLogin,
               minWidth: 80,
               isSortable: true,
             },
           ].filter(c => !!c)}
           defaultSort={{
             column: 'amount',
             desc: true,
           }}
           searches={[
             {
               title: 'Name',
               name: 'displayName',
               isFuzzy: true,
             },
           ].filter(s => !!s)}
           rowIdentifier={'id'}
           rowOnClick={deactivatedNote === '' && onRowClick}
           rowOnClickDisabled={rowOnClickDisabled}
           iconsKey={[
             {
               image: 'dotNew',
               title: 'Is New',
               color: '#00D0BE',
               description: 'Indicates New Household',
               displayCondition: h => !!h.isNew,
             },
           ]}
           filters={[
             <Checkbox
               key={'isNew'}
               title={'New'}
               dataPoint={{
                 name: 'isNew',
                 value: !!filters.isNew,
               }}
               onChange={(name, value) => updateFilters(name, value || undefined)}
               disabled={false}/>,
           ].filter(f => !!f)}
           rowsPerPage={rowsPerPage}
           filterValues={filters}
           maxHeight={maxHeight}
           height={height}/>
  </Column>;
};

const mapStateToProps = state => ({
  session: state.session,
});

const mapDispatchToProps = (dispatch) => ({
  openModal: modalToOpen => dispatch(_openModal(modalToOpen)),
});

export default compose(withSize(), connect(mapStateToProps, mapDispatchToProps))(HouseholdsTable);

HouseholdsTable.propTypes = {
  /**
   * SizeMe size
   */
  size: PropTypes.shape({
    width: PropTypes.number,
  }),
  openModal: PropTypes.func.isRequired,
  context: PropTypes.shape({
    id: PropTypes.number.isRequired,
    type: PropTypes.string.isRequired,
    containerID: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  /**
   * Optional function to be called on row click
   */
  onRowClick: PropTypes.func,
  /**
   * Optional function to disable on row click
   */
  rowOnClickDisabled: PropTypes.func,
  /**
   * Optional rows per page override
   */
  rowsPerPage: PropTypes.number,
  /**
   * Optional css max height for table
   */
  maxHeight: PropTypes.string,
  /**
   * Optional css height for table defaults to 200px
   */
  height: PropTypes.string,
  session: PropTypes.object,
  /**
   * Indicates this is a full page and should not have size restrictions
   */
  fullPage: PropTypes.bool,
};
